import { useState } from "react";
import { api, ApiError } from "@/lib/apiClient";
import { validateReturnNotes } from "@/lib/validators";
import type { Reservation, ReturnCondition } from "@/types/reservation";

const CONDITION_OPTIONS: { value: ReturnCondition; label: string }[] = [
  { value: "no_problem", label: "問題なし" },
  { value: "problem", label: "不具合あり" },
  { value: "missing_accessory", label: "備品不足あり" },
];

export function ReturnModal({
  reservation,
  pcName,
  onClose,
  onReturned,
}: {
  reservation: Reservation;
  pcName: string;
  onClose: () => void;
  onReturned: (updated: Reservation) => void;
}) {
  const [condition, setCondition] = useState<ReturnCondition>("no_problem");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (submitting) return;
    const validationError = validateReturnNotes(notes);
    if (validationError) {
      setError(validationError);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const updated = await api.post<Reservation>(`/reservations/${reservation.id}/return`, {
        condition,
        notes: notes || null,
      });
      onReturned(updated);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "返却処理に失敗しました。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="section-title" style={{ margin: 0 }}>
            PCを返却しました
          </h2>
          <button className="modal-close" onClick={onClose} aria-label="閉じる">
            ×
          </button>
        </div>

        <p style={{ color: "var(--color-text-muted)", marginTop: 0 }}>{pcName} の返却を記録します。</p>

        <div className="form-field">
          <label>PCの状態</label>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {CONDITION_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 12px",
                  border: "1px solid var(--color-border)",
                  borderRadius: 10,
                  fontSize: 14,
                }}
              >
                <input
                  type="radio"
                  name="condition"
                  checked={condition === opt.value}
                  onChange={() => setCondition(opt.value)}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>

        <div className="form-field">
          <label>備考</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={1000} />
        </div>

        {error && <p className="error-text">{error}</p>}

        <div className="form-row">
          <button className="btn btn-secondary btn-block" onClick={onClose} disabled={submitting}>
            キャンセル
          </button>
          <button className="btn btn-primary btn-block" onClick={submit} disabled={submitting}>
            {submitting ? "送信中..." : "返却を確定する"}
          </button>
        </div>
      </div>
    </div>
  );
}
